import { BoxGeometry, Group, Mesh } from 'three';
import type { HouseConfig } from '@/model/schema';
import type { MaterialKit } from '../materials';

/**
 * Walkway from the front door out to the entrance gate (or, when there
 * is no fence, straight down to the front edge of the lot). Same paving
 * material as the driveway so the hardscape reads as one system.
 *
 * Shape
 * -----
 * When the front door and the gate are within `JOIN_TOL` of each other
 * in X we emit a single longitudinal slab — the straight shot.
 *
 * Otherwise we emit a THREE-leg "Z" path:
 *
 *   door ─────┐  leg 1 : vertical, centred on the door X
 *             │
 *             └─────┐  leg 2 : lateral turn INSIDE the lot
 *                   │
 *                   │  leg 3 : vertical, centred on the gate X
 *                   ▼
 *                 gate
 *
 * The turn happens `TURN_INSET` metres in front of the fence — not AT
 * the fence — so the path doesn't visually "slice through" the fence
 * line when approached from the front. That matches how real walkways
 * take their dogleg well clear of the gate rather than right next to
 * it. When the yard is shallow we fall back to half the yard depth so
 * the turn still fits cleanly between house and fence.
 *
 * Mailbox avoidance
 * -----------------
 * If the door-X column would plough straight into the mailbox post,
 * we nudge leg 1 (and, when there's no dogleg, the whole walkway)
 * sideways by just enough to brush past. The generator's
 * MAILBOX_GATE_GAP keeps the mailbox clear of the gate X so the
 * gate-side leg 3 is always unobstructed.
 *
 * All legs sit just above the lot base plate (1 mm lift) and use
 * `kit.driveway`. The whole walkway is one `Walkway` group so the
 * "house only" toggle can hide it together with the rest of the site
 * furniture.
 */
const SLAB_THICKNESS = 0.05;
const LIFT = 0.001;
const WALK_WIDTH = 1.1;
const JOIN_TOL = 0.05;
/** Ideal distance from the fence line at which the walkway makes its
 *  lateral turn. Clamped by half the yard depth for shallow lots. */
const TURN_INSET = 2.2;
const MIN_TURN_INSET = 1.0;

export function buildWalkway(cfg: HouseConfig, kit: MaterialKit): Group {
  const group = new Group();
  group.name = 'Walkway';

  const frontDoor = cfg.stories[0]?.doors.find((d) => d.side === 'front' && d.role === 'front')
    ?? cfg.stories[0]?.doors.find((d) => d.side === 'front');
  if (!frontDoor) return group;

  const doorX = cfg.house.originX + frontDoor.offset + frontDoor.width / 2;
  const houseFrontZ = cfg.house.originZ;
  const lotFrontZ = -cfg.lot.depth / 2;

  // "Destination" X at the lot front edge. With a fence, snap to the
  // gate centre X (so people don't have to climb the fence); without
  // a fence, fall back to the door X (straight shot to the street).
  let destX = doorX;
  if (cfg.fence) {
    const gateWidth = 1.0;
    destX = -cfg.lot.width / 2 + cfg.fence.gateOffset + gateWidth / 2;
  }

  // Mailbox avoidance on the door-side leg. Only applies to the
  // longitudinal run at doorX; the gate-side leg is kept clear of the
  // mailbox by MAILBOX_GATE_GAP in the generator.
  let legAx = doorX;
  const mailbox = cfg.mailbox;
  if (mailbox) {
    const halfWalk = WALK_WIDTH / 2;
    const halfMb = 0.18;
    const requiredGap = halfWalk + halfMb + 0.05;
    const dx = mailbox.x - legAx;
    if (Math.abs(dx) < requiredGap) {
      const shift = requiredGap - Math.abs(dx);
      legAx += dx >= 0 ? -shift : +shift;
    }
  }

  const yardDepth = Math.max(0.1, houseFrontZ - lotFrontZ);

  // No meaningful offset between door and gate → one straight slab.
  if (Math.abs(destX - legAx) <= JOIN_TOL) {
    addLeg(
      group,
      'Walkway_Straight',
      legAx,
      WALK_WIDTH,
      (houseFrontZ + lotFrontZ) / 2,
      yardDepth,
    );
    return group;
  }

  // Dogleg (Z) path. Pick a turn Z that sits IN FRONT of the fence by
  // TURN_INSET, clamped to half the yard depth so the turn stays
  // visibly inside the lot on shallow yards (a turn at the very edge
  // of the fence is exactly the unnatural look the user complained
  // about).
  const turnInset = Math.max(
    MIN_TURN_INSET,
    Math.min(TURN_INSET, yardDepth * 0.5),
  );
  const turnZ = lotFrontZ + turnInset;

  // Leg 1: door column, from houseFrontZ down to turnZ (plus half a
  // walk-width extra so the corner tile covers cleanly).
  const leg1Len = Math.max(0.1, houseFrontZ - turnZ + WALK_WIDTH / 2);
  addLeg(
    group,
    'Walkway_Leg1',
    legAx,
    WALK_WIDTH,
    (houseFrontZ + (turnZ - WALK_WIDTH / 2)) / 2,
    leg1Len,
  );

  // Leg 2: lateral connector at turnZ, padded half a walk-width on
  // each end so the two corners with legs 1 & 3 overlap cleanly.
  const lateralLo = Math.min(legAx, destX) - WALK_WIDTH / 2;
  const lateralHi = Math.max(legAx, destX) + WALK_WIDTH / 2;
  const lateralLen = lateralHi - lateralLo;
  const leg2 = new Mesh(
    new BoxGeometry(lateralLen, SLAB_THICKNESS, WALK_WIDTH),
    kit.driveway,
  );
  leg2.name = 'Walkway_Leg2';
  leg2.receiveShadow = true;
  leg2.castShadow = false;
  leg2.position.set(
    (lateralLo + lateralHi) / 2,
    SLAB_THICKNESS / 2 + LIFT,
    turnZ,
  );
  group.add(leg2);

  // Leg 3: gate column, from turnZ down to the lot front edge. Again
  // padded half a walk-width into the corner so it meshes with leg 2.
  const leg3StartZ = turnZ + WALK_WIDTH / 2;
  const leg3Len = Math.max(0.1, leg3StartZ - lotFrontZ);
  addLeg(
    group,
    'Walkway_Leg3',
    destX,
    WALK_WIDTH,
    (leg3StartZ + lotFrontZ) / 2,
    leg3Len,
  );

  return group;

  /* eslint-disable-next-line no-inner-declarations */
  function addLeg(
    parent: Group,
    name: string,
    cx: number,
    sx: number,
    cz: number,
    sz: number,
  ): void {
    const mesh = new Mesh(
      new BoxGeometry(sx, SLAB_THICKNESS, sz),
      kit.driveway,
    );
    mesh.name = name;
    mesh.receiveShadow = true;
    mesh.castShadow = false;
    mesh.position.set(cx, SLAB_THICKNESS / 2 + LIFT, cz);
    parent.add(mesh);
  }
}
